import React from 'react';
import { Phone, Mail, MapPin } from 'lucide-react';
import { cabinetInfo } from '../../mock';

const sections = [
  {
    title: 'Soins',
    links: [
      { label: 'Soins dentaires', href: '#services' },
      { label: 'Implants', href: '#specialites' },
      { label: 'Orthodontie', href: '#services' },
      { label: 'Gouttières', href: '#specialites' },
    ],
  },
  {
    title: 'Cabinet',
    links: [
      { label: 'Équipe', href: '#equipe' },
      { label: 'Technologies', href: '#technologies' },
      { label: 'Galerie', href: '#galerie' },
      { label: 'FAQ', href: '#faq' },
    ],
  },
];

const year = new Date().getFullYear();

export default function FooterV2({ scrollToSection }) {
  const goTo = (href) => (e) => {
    if (href.startsWith('#')) {
      e.preventDefault();
      scrollToSection(href.slice(1));
    }
  };

  return (
    <footer className="bg-muted/40 border-t border-foreground/10 py-20 lg:py-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <h2
          aria-hidden="true"
          className="font-display text-7xl sm:text-8xl lg:text-[10rem] xl:text-[12rem] leading-[0.85] tracking-tight text-foreground select-none"
        >
          Pluridental <span className="italic text-foreground/55">62</span>.
        </h2>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-y-10 md:gap-x-8">
          <div className="md:col-span-5 space-y-5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-foreground/55">
              Cabinet dentaire — Courcelles-lès-Lens
            </p>
            <p className="max-w-md text-sm text-foreground/65 leading-relaxed">
              Soins, implants, orthodontie et gouttières transparentes. Un cabinet pluridisciplinaire
              à 5 minutes de Lens et Hénin-Beaumont.
            </p>

            <ul className="space-y-3 text-sm">
              <li>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(cabinetInfo.address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-start gap-2.5 text-foreground/75 hover:text-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground rounded"
                >
                  <MapPin size={14} className="mt-1 shrink-0 text-amber-600" aria-hidden="true" />
                  {cabinetInfo.address}
                </a>
              </li>
              {cabinetInfo.phone.map((num, i) => (
                <li key={i}>
                  <a
                    href={`tel:${num.replace(/\s/g, '')}`}
                    className="inline-flex items-center gap-2.5 text-foreground/75 hover:text-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground rounded"
                  >
                    <Phone size={14} className="shrink-0 text-amber-600" aria-hidden="true" />
                    {num}
                  </a>
                </li>
              ))}
              <li>
                <a
                  href={`mailto:${cabinetInfo.email}`}
                  className="inline-flex items-center gap-2.5 text-foreground/75 hover:text-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground rounded break-all"
                >
                  <Mail size={14} className="shrink-0 text-amber-600" aria-hidden="true" />
                  {cabinetInfo.email}
                </a>
              </li>
            </ul>
          </div>

          <div className="md:col-span-7 grid sm:grid-cols-3 gap-8">
            {sections.map((section) => (
              <div key={section.title}>
                <h4 className="text-[11px] font-semibold uppercase tracking-[0.3em] text-foreground/55 mb-5">
                  {section.title}
                </h4>
                <ul className="space-y-3">
                  {section.links.map((link) => (
                    <li key={link.label}>
                      <a
                        href={link.href}
                        onClick={goTo(link.href)}
                        className="text-sm text-foreground/75 hover:text-foreground hover:underline underline-offset-4 decoration-amber-500 decoration-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground rounded"
                      >
                        {link.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            <div>
              <h4 className="text-[11px] font-semibold uppercase tracking-[0.3em] text-foreground/55 mb-5">
                Horaires
              </h4>
              <ul className="space-y-2 text-sm">
                {cabinetInfo.hours.map((h, i) => (
                  <li key={i} className="flex items-baseline justify-between gap-2 text-foreground/75">
                    <span className="text-xs uppercase tracking-wider font-semibold text-foreground/85">{h.day}</span>
                    <span className="tabular-nums text-[11px] text-foreground/55">{h.time.split(' / ')[0]}</span>
                  </li>
                ))}
                <li className="pt-2 border-t border-foreground/10 text-[10px] uppercase tracking-[0.2em] text-foreground/40">
                  Sam. & Dim. fermé
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-6 border-t border-foreground/10 flex flex-col md:flex-row gap-4 md:items-center md:justify-between text-[10px] uppercase tracking-[0.2em] text-foreground/40">
          <p>© {year} Cabinet Dentaire Pluridental 62 — Tous droits réservés</p>
          <ul className="flex flex-wrap gap-x-6 gap-y-2">
            <li>
              <a
                href="https://annuaire.ordre-chirurgiens-dentistes.fr/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-foreground hover:underline underline-offset-4 decoration-amber-500 decoration-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground rounded"
              >
                ONCD
              </a>
            </li>
            <li>
              <a
                href="#contact"
                onClick={goTo('#contact')}
                className="hover:text-foreground hover:underline underline-offset-4 decoration-amber-500 decoration-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground rounded"
              >
                Mentions légales
              </a>
            </li>
            <li>
              <a
                href="#contact"
                onClick={goTo('#contact')}
                className="hover:text-foreground hover:underline underline-offset-4 decoration-amber-500 decoration-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground rounded"
              >
                Confidentialité
              </a>
            </li>
          </ul>
        </div>
      </div>
    </footer>
  );
}
